import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../../store/store';
import { fetchCars, fetchDrivers, addCar, updateCar, addDriver, updateDriver } from '../../store/actions';
import { refreshDriversAfterAdd, deleteDriver } from '../../store/slices/driverSlice';
import { fetchAllFlashDeals,clearCurrentFlashDeal, clearFormData, fetchCurrentFlashingDeals,fetchAllActiveFlashDeals,fetchInactiveFlashDeals,setCurrentFilter } from '../../store/slices/flashDealsSlice';
import { Car, Driver } from '../../store/types';
import { Divider, IconButton, Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Snackbar, Alert,Checkbox,FormControlLabel, FormControl,SelectChangeEvent} from '@mui/material';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';

import format from 'date-fns/format';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';

import {
  StyledPaper,
  SectionContainer,
  SectionTitle,
  ContentWrapper,
} from './styles/RightPanel.styles';
import ResizablePanel from './ResizablePanel';
import { StyledDataGrid } from './styles/DataGrid.styles';
import ViewDialog from './ViewDialog';
import EditDialog from './EditDialog';

const sharedGridProps = {
  autoHeight: false,
  headerHeight: 40,
  rowHeight: 35,
  hideFooter: true,
  disableRowSelectionOnClick: true,
  disableColumnMenu: true,
  disableExtendRowFullWidth: false,
  density: 'compact' as const,
};


interface RightPanelProps {
  onTripClick?: (trip: any) => void;
}

const RightPanel: React.FC<RightPanelProps> = ({ onTripClick }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { items: cars, loading: carsLoading } = useSelector((state: RootState) => state.cars);
  const { items: drivers, loading: driversLoading } = useSelector((state: RootState) => state.drivers);
  const { flashDeals, loading: flashDealsLoading,currentFilter } = useSelector((state: RootState) => state.flashDeals);
  const { user } = useSelector((state: RootState) => state.auth);
  const userRole = useSelector((state: RootState) => state.auth.user?.role);

  useEffect(() => {
    dispatch(fetchCars());
    dispatch(fetchDrivers());
    dispatch(fetchAllFlashDeals());
  }, [dispatch]);

  // Helper function to check if current user is admin
  const isAdmin = user?.role === 'ADMIN';

  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<{ id: number, type: 'car' | 'driver' } | null>(null);
  const [selectedItem, setSelectedItem] = useState<Car | Driver | null>(null);
  const [isNewItem, setIsNewItem] = useState(false);
  const [dialogType, setDialogType] = useState<'car' | 'driver'>('car');
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);

  const handleView = (item: Car | Driver, type: 'car' | 'driver') => {
    setSelectedItem(item);
    setDialogType(type);
    setViewDialogOpen(true);
  };

  const handleEdit = (item: Car | Driver | null, type: 'car' | 'driver', isNew: boolean = false) => {
    setSelectedItem(item);
    setDialogType(type);
    setIsNewItem(isNew);
    setEditDialogOpen(true);
  };

 const handleSelectedOptionChange = async (event: SelectChangeEvent<string>) => {
    const selectedOption = event.target.value as 'all' | 'active' | 'inactive' | 'currentFlashing';
    
    // Dispatch to Redux to persist the filter
    dispatch(setCurrentFilter(selectedOption));
    
    const currentDateTime = format(new Date(), "yyyy-MM-dd'T'HH:mm:ss");
    
    if(selectedOption === 'active'){
        await dispatch(fetchAllActiveFlashDeals());
    }else if(selectedOption === 'inactive'){
      await dispatch(fetchInactiveFlashDeals());
    }else if(selectedOption === 'currentFlashing'){
      await dispatch(fetchCurrentFlashingDeals(currentDateTime));
    }else{
     await dispatch(fetchAllFlashDeals());
    }
 }

  // Delete functionality removed as requested

  const handleSave = async (data: Car | Driver) => {
    let saveError: any = null;
    let fetchError: any = null;

    try {
      if (dialogType === 'car') {
        if (isNewItem) {
          await dispatch(addCar(data as Omit<Car, 'id'>));
        } else {
          await dispatch(updateCar({ id: (data as Car).id, car: data as Car }));
        }
      } else {
        if (isNewItem) {
          await dispatch(addDriver(data as Omit<Driver, 'id'>));
          // Use the new refreshDriversAfterAdd action for better state management
          await dispatch(refreshDriversAfterAdd());
        } else {
          // Ensure we have the ID from either the data or the original selectedItem
          const driverId = (data as Driver).id || (selectedItem as Driver)?.id;

          if (!driverId) {
            throw new Error('Driver ID is missing for update operation');
          }

          // Ensure the data includes the ID
          const driverWithId = { ...data, id: driverId } as Driver;
          await dispatch(updateDriver({ id: driverId, driver: driverWithId }));
        }
      }
    } catch (error: any) {
      console.error('Error saving data:', error);
      saveError = error;
    } finally {
      // Always refresh the data to ensure UI is up to date
      try {
        if (dialogType === 'driver') {
          if (!isNewItem) { // For new drivers, we already refreshed with refreshDriversAfterAdd
            await dispatch(fetchDrivers());
          }
        } else {
          await dispatch(fetchCars());
        }
      } catch (error: any) {
        console.error('Error refreshing data:', error);
        fetchError = error;
      }

      // Close the dialog regardless of errors
      setEditDialogOpen(false);

      // Re-throw any errors after refreshing and closing
      if (saveError) throw saveError;
      if (fetchError) throw fetchError;
    }
  };

  // Show delete confirmation dialog
  const showDeleteConfirmation = (id: number, type: 'car' | 'driver') => {
    setItemToDelete({ id, type });
    setDeleteDialogOpen(true);
  };

  // Handle cancel delete
  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setItemToDelete(null);
  };

  // Implement delete functionality for drivers
  const handleDelete = async () => {
    if (!itemToDelete) return;

    // Close the dialog immediately to prevent it from reappearing
    setDeleteDialogOpen(false);

    const { id, type } = itemToDelete;
    let deleteError: any = null;
    let fetchError: any = null;

    try {
      if (type === 'driver') {
        const result = await dispatch(deleteDriver(id));

        // Show success message from API response
        if (result.payload && typeof result.payload === 'object' && 'message' in result.payload) {
          setSuccessMessage(result.payload.message as string);
          setShowSuccessAlert(true);
        } else {
          setSuccessMessage('Driver deleted successfully');
          setShowSuccessAlert(true);
        }
      }
    } catch (error: any) {
      console.error('Error deleting item:', error);
      deleteError = error;
    } finally {
      // Always refresh the data to ensure UI is up to date
      try {
        if (type === 'driver') {
          await dispatch(fetchDrivers());
        }
      } catch (error: any) {
        console.error('Error refreshing data after delete:', error);
        fetchError = error;
      }

      // Clear the item to delete
      setItemToDelete(null);

      // Re-throw any errors after refreshing
      if (deleteError) throw deleteError;
      if (fetchError) throw fetchError;
    }
  };

  const handleFlashDealClick = (flashDeal: any) => {
    if (onTripClick) {
      const flashDealObj = {
        tripId: flashDeal.dealCode,
        tripType: 'FLASH_DEALS',
        dealType: flashDeal.dealType,
        mode: 'edit',
        dealCode: flashDeal.dealCode,
        ...flashDeal
      };
      onTripClick(flashDealObj);
    }
  };

  const handleCreateFlashDeal = () => {
    dispatch(clearCurrentFlashDeal());
    dispatch(clearFormData());
    if (onTripClick) {
      const flashDealObj = {
        tripId: 'FlashDeals',
        tripType: 'FLASH_DEALS',
        mode: 'create',
        dealCode: null  // Ensure no deal code for fresh form
      };
      onTripClick(flashDealObj);
    }
  };

  const handleCashTransactionComp = () => {
      if(onTripClick){
         const cashComponentObj = {
           tripId: "CashManagement",
           tripType : "CASH_COMPONENT"
         };
         onTripClick(cashComponentObj)
      }
  }
  const handleCashreportsComp = () => {
      if(onTripClick){
         const cashReportsCompObj = {
           tripId: "CashReports",
           tripType : "CASH_REPORTS"
         };
         onTripClick(cashReportsCompObj)
      }
  }



  const flashDealsColumns: GridColDef[] = [
    {
      field: "dealCode",
      headerName: "Code",
      flex: 0.5,
      minWidth: 80,
      renderCell: (params: any) => {
        const dealCode = params.value || 'N/A';
        return (
          <span style={{ fontSize: '0.8rem', color: 'rgba(255, 255, 255, 0.9)' }}>
            {dealCode}
          </span>
        );
      }
    },
    {
      field: "dealType",
      headerName: "Type",
      flex: 1,
      minWidth: 80,
      renderCell: (params: any) => {
        const dealType = params.value;
        return (
          <span style={{ fontSize: '0.8rem', color: 'rgba(255, 255, 255, 0.9)' }}>
            {dealType === 1 ? 'One Way' : dealType === 2 ? 'Shared' : 'Unknown'}
          </span>
        );
      }
    },

    {
      field: "dealStartDatetime",
      headerName: "Start",
      flex: 1,
      minWidth: 100,
      renderCell: (params: any) => {
        const date = new Date(params.value);
        return (
          <span style={{ fontSize: '0.8rem' }}>
            {date.toLocaleDateString('en-IN', {
              day: '2-digit',
              month: 'short',
              hour: '2-digit',
              minute: '2-digit',
              hour12: false,
              }).replace(',', '')}
          </span>
        );
      }
    },
    {
      field: "dealEndDatetime",
      headerName: "End",
      flex: 1,
      minWidth: 100,
      renderCell: (params: any) => {
        const date = new Date(params.value);
        return (
          <span style={{ fontSize: '0.8rem' }}>
            {date.toLocaleDateString('en-IN', {
              day: '2-digit',
              month: 'short',
              hour: '2-digit',
              minute: '2-digit',
              hour12: false
            }).replace(',', '')}
          </span>
        );
      }
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      minWidth: 100,
      renderCell: (params: any) => {
        const isActive = params.row.isActive;
        return (
          <span style={{ fontSize: '0.8rem', color: isActive ? '#4caf50' : '#f44336' }}>
            {isActive ? 'Active' : 'Inactive'}
          </span>
        );
      }
    }
  ];
  const carColumns: GridColDef[] = [
    { field: 'makeModel', headerName: 'Car', flex: 1 },
    { field: 'plateNumber', headerName: 'Plate Number', flex: 1 },
    { field: 'year', headerName: 'Year', flex: 1 },
  ];

  const driverColumns: GridColDef[] = [
    { field: 'fullName', headerName: 'Name', flex: 1 },
    { field: 'licenseNumber', headerName: 'License', flex: 1 },
    { field: 'mobileNumber', headerName: 'Mobile', flex: 1 },
    { field: 'status', headerName: 'Status', flex: 1 },
    ...(isAdmin ? [{
      field: 'actions',
      headerName: 'Actions',
      width: 100,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params: GridRenderCellParams) => {
        const driver = params.row as Driver;
        return (
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation(); // Prevent row selection
                handleEdit(driver, 'driver', false);
              }}
              sx={{ color: '#1976d2' }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation(); // Prevent row selection
                showDeleteConfirmation(driver.id, 'driver');
              }}
              sx={{ color: '#ff4d4f' }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        );
      },
    }] : []),
  ];

  const [panelWidth, setPanelWidth] = useState(450);
  const sectionHeights = {
    cars: 400,
    drivers: 400,
    flashDeals: 400,
  };

  if (carsLoading || driversLoading) {
    return <div>Loading...</div>;
  }

  // Delete confirmation dialog component
  const DeleteConfirmationDialog = () => {
    if (!itemToDelete) return null;

    const { type } = itemToDelete;
    const itemType = type === 'driver' ? 'Driver' : 'Car';

    return (
      <>
        <Dialog
          open={deleteDialogOpen}
          onClose={handleCancelDelete}
          aria-labelledby="delete-dialog-title"
          aria-describedby="delete-dialog-description"
        >
          <DialogTitle id="delete-dialog-title">
            {`Delete ${itemType}`}
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="delete-dialog-description">
              Are you sure you want to delete this {itemType.toLowerCase()}? This action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancelDelete} color="primary">
              Cancel
            </Button>
            <Button onClick={handleDelete} color="error" autoFocus>
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  };

  // Success message snackbar component
  const SuccessMessageSnackbar = () => {
    return (
      <Snackbar
        open={showSuccessAlert}
        autoHideDuration={4000}
        onClose={() => setShowSuccessAlert(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setShowSuccessAlert(false)}
          severity="success"
          sx={{ width: '100%' }}
        >
          {successMessage}
        </Alert>
      </Snackbar>
    );
  };

  const sortedFlashDeals = [...flashDeals].sort((a, b) => {
    return new Date(a.dealStartDatetime).getTime() - new Date(b.dealStartDatetime).getTime();
  });

  return (
    <ResizablePanel
      width={panelWidth}
      height={window.innerHeight}
      onWidthChange={setPanelWidth}
      minWidth={250}
      maxWidth={600}
      resizeHandlePosition="left"
    >
      <StyledPaper
        elevation={3}
        sx={{ width: panelWidth, overflowX: 'auto', overflowY: 'auto' }}
      >
        <Box sx={{ p: 2, pb: 1, borderBottom: '1px solid rgba(255, 255, 255, 0.12)' }}>
          <Box sx={{ display: 'flex', flexWrap:'wrap', gap: 1 }}>
            {userRole !== 'OPERATOR' && <Box sx={{ flex: '1 1 auto', minWidth: '150px' }}>
              <Button
                startIcon={<AccountBalanceIcon />}
                variant="contained"
                size="medium"
                fullWidth
                onClick={handleCashTransactionComp}
                sx={{
                  backgroundColor: '#007acc',
                  '&:hover': { backgroundColor: '#005999' },
                  textTransform: 'none',
                  fontWeight: 600
                }}
              >
                Cash Management
              </Button>
            </Box>}
            {(userRole === 'ADMIN' || userRole === 'OPERATOR') && <Box sx={{ flex: '1 1 auto', minWidth: '150px' }}>
              <Button
                startIcon={<AccountBalanceIcon />}
                variant="contained"
                size="medium"
                fullWidth
                onClick={handleCashreportsComp}
                sx={{
                  backgroundColor: '#007acc',
                  '&:hover': { backgroundColor: '#005999' },
                  textTransform: 'none',
                  fontWeight: 600
                }}
              >
                Cash Reports
              </Button>
            </Box>}
          </Box>
        </Box>
        <Box sx={{ p: 2,pb:0 }}>
          <Button
            startIcon={<AddIcon />}
            variant="contained"
            size="medium"
            fullWidth
            onClick={handleCreateFlashDeal}
            sx={{ 
              backgroundColor: '#007acc', 
              '&:hover': { backgroundColor: '#005999' },
              textTransform: 'none',
              fontWeight: 600
            }}
          >
            Create Flash Deal
          </Button>
        </Box>
        <ContentWrapper>
          <Box >
            <SectionContainer>
              <Box sx={{height:"auto"}}>
              <Box mb={1}>
                {/* Title Row */}
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={0}>
                  <SectionTitle>Flash Deals{flashDeals && flashDeals.length > 0 && ` (${flashDeals.length})`}</SectionTitle>
                </Box>
                {/* Checkboxes Row */}
                <Box display="flex" justifyContent="flex-end" alignItems="center" gap={0.5} flexWrap="wrap">
                  {/* <FormControlLabel
                    control={
                      <Checkbox
                        checked={showCurrentFlashing}
                        onChange={handleCheckboxChange}
                        color="primary"
                        size="small"
                      />
                    }
                    label="Current Flashing"
                    labelPlacement="start"
                    sx={{ 
                      margin: 0,
                      '& .MuiFormControlLabel-label': {
                        fontSize: '0.75rem',
                        marginRight: '2px'
                      }
                    }}
                  /> */}
                  <FormControl size="small" sx={{ minWidth: 100 }}>
                    <Select
                      value={currentFilter}
                      onChange={handleSelectedOptionChange}
                      displayEmpty
                      sx={{
                        fontSize: '0.75rem',
                        height: '28px',
                        backgroundColor: 'rgba(255, 255, 255, 0.05)',
                        color: 'white',
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'rgba(255, 255, 255, 0.23)'
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'rgba(255, 255, 255, 0.4)'
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#1976d2'
                        },
                        '& .MuiSvgIcon-root': {
                          color: 'white'
                        }
                      }}
                    >
                      <MenuItem value="all" sx={{ fontSize: '0.75rem' }}>All</MenuItem>
                      <MenuItem value="active" sx={{ fontSize: '0.75rem' }}>Active</MenuItem>
                      <MenuItem value="inactive" sx={{ fontSize: '0.75rem' }}>Inactive</MenuItem>
                      <MenuItem value="currentFlashing" sx={{fontSize: '0.75rem' }}>Current Flashing</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              </Box>
              <Divider sx={{ backgroundColor: 'rgba(255, 255, 255, 0.12)', mb: 2 }} />
              {flashDeals && flashDeals.length > 0 ? (
                <StyledDataGrid
                  sx={{ width: '100%', height: '350px' }}
                  rows={sortedFlashDeals}
                  columns={flashDealsColumns}
                  getRowId={(row) => row.id || Math.random().toString()}
                  onRowClick={(params) => handleFlashDealClick(params.row)}
                  {...sharedGridProps}
                />
              ) : (
                <Box sx={{ textAlign: 'center', color: 'rgba(255, 255, 255, 0.7)', mt: 4 }}>
                  No Flash Deals Available
                </Box>
              )}
                </Box>

            </SectionContainer>
          </Box>
          <Box height={sectionHeights.cars}>
            <SectionContainer>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <SectionTitle>Cars</SectionTitle>
                <Button
                  startIcon={<AddIcon />}
                  variant="contained"
                  size="small"
                  disabled={true}
                  sx={{ opacity: 0.5 }}
                >
                  Add Car
                </Button>
              </Box>
              <Divider sx={{ backgroundColor: 'rgba(255, 255, 255, 0.12)', mb: 2 }} />
              <StyledDataGrid
                sx={{ width: '100%' }}
                rows={cars}
                columns={carColumns}
                getRowId={(row) => row.id || Math.random().toString()}
                onRowClick={(params) => handleView(params.row as Car, 'car')}
                {...sharedGridProps}
              />
            </SectionContainer>
          </Box>

          <Box height={sectionHeights.drivers}>
            <SectionContainer>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <SectionTitle>Drivers</SectionTitle>
                {isAdmin && (
                  <Button
                    startIcon={<AddIcon />}
                    variant="contained"
                    size="small"
                    onClick={() => handleEdit(null, 'driver', true)}
                    sx={{ backgroundColor: '#007acc', '&:hover': { backgroundColor: '#005999' } }}
                  >
                    Add Driver
                  </Button>
                )}
              </Box>
              <Divider sx={{ backgroundColor: 'rgba(255, 255, 255, 0.12)', mb: 2 }} />
              <StyledDataGrid
                sx={{ width: '100%' }}
                rows={drivers}
                columns={driverColumns}
                getRowId={(row) => row.id || Math.random().toString()}
                onRowClick={(params) => handleView(params.row as Driver, 'driver')}
                {...sharedGridProps}
              />
            </SectionContainer>
          </Box>


        </ContentWrapper>

        <>
          {selectedItem && (
            <>
              <ViewDialog
                open={viewDialogOpen}
                onClose={() => setViewDialogOpen(false)}
                title={dialogType === 'car' ? 'Car Details' : `${(selectedItem as Driver).firstName} ${(selectedItem as Driver).lastName}`}
                data={selectedItem}
              />
              {/* Delete functionality removed as requested */}
            </>
          )}
          <EditDialog
            open={editDialogOpen}
            onClose={() => setEditDialogOpen(false)}
            onSave={handleSave}
            title={`${isNewItem ? 'Add' : 'Edit'} ${dialogType === 'car' ? 'Car' : 'Driver'}`}
            data={selectedItem}
            isNew={isNewItem}
            dialogType={dialogType}
          />
        </>
      </StyledPaper>
      <DeleteConfirmationDialog />
      <SuccessMessageSnackbar />
    </ResizablePanel>
  );
};

export default RightPanel;
